import { createContext, useCallback, useContext, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AuthAccount,
  fetchSession,
  login as startLogin,
  loginToTenant as startTenantLogin,
  logout as startLogout,
} from '@/lib/auth'

/** The cache entry the whole app reads its identity from. One query, fetched once, on load. */
export const authKeys = {
  session: ['bff', 'user'] as const,
}

interface AuthState {
  /** Whether the session question has been answered at all — either way. */
  isInitialized: boolean
  /** Whether a session exists. */
  isAuthenticated: boolean
  /** Whether the answer is still being fetched. */
  isLoading: boolean
  /** The signed-in user, or null. */
  account: AuthAccount | null
  displayName: string
  email: string
  /** Set when asking who we are *failed* — which is not the same as being signed out. */
  error: Error | null
}

interface AuthActions {
  /** Navigate to the BFF to start a sign-in. */
  login: (returnTo?: string) => void
  /** Start a sign-in scoped to one tenant, from the /t/{slug} entry point. */
  loginToTenant: (slug: string, returnTo?: string) => void
  /** End the session here and at the identity provider. */
  logout: () => Promise<void>
}

type UseAuthReturn = AuthState & AuthActions

const AuthContext = createContext<UseAuthReturn | null>(null)

/**
 * Owns the app's single answer to "who is signed in".
 *
 * <p>
 * Before the BFF this provider started an OIDC client, completed in-flight redirects and kept tokens
 * alive on a timer. There is nothing to start any more: the session is a cookie the browser sends by
 * itself, and the question is one <code>GET /bff/user</code>.
 * </p>
 * <p>
 * It is still a provider rather than a hook each component calls, and still for a concurrency reason —
 * just a milder one. A TanStack Query at a fixed key gives every consumer the same in-flight request and
 * the same answer, instead of one request per component mounting on load.
 * </p>
 * <p>
 * A 401 resolves to <code>null</code> rather than to an error, because signed out is an answer. Anything
 * else — a network failure, a malformed body — is an error, and the two must stay distinguishable: a
 * broken gateway rendered as "please sign in" sends the user round a loop that cannot end.
 * </p>
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()

  const session = useQuery<AuthAccount | null>({
    queryKey: authKeys.session,
    queryFn: fetchSession,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  })

  const login = useCallback((returnTo?: string) => startLogin(returnTo), [])
  const loginToTenant = useCallback((slug: string, returnTo?: string) => startTenantLogin(slug, returnTo), [])

  const logout = useCallback(async () => {
    await startLogout()
    // Only reached when the sign-out succeeded — `startLogout` throws otherwise and deliberately does not
    // navigate, because leaving a live session behind a signed-out-looking shell is the worse failure.
    queryClient.removeQueries({ queryKey: authKeys.session })
  }, [queryClient])

  const account = session.data ?? null

  const value = useMemo<UseAuthReturn>(
    () => ({
      isInitialized: !session.isPending,
      isAuthenticated: account !== null,
      isLoading: session.isPending,
      account,
      displayName: account?.displayName ?? '',
      email: account?.email ?? '',
      error: session.error instanceof Error ? session.error : null,
      login,
      loginToTenant,
      logout,
    }),
    [session.isPending, session.error, account, login, loginToTenant, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * Authentication state and actions for the current session.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isAuthenticated, isLoading, login, logout, displayName } = useAuth()
 *
 *   if (isLoading) return <Spinner />
 *   if (!isAuthenticated) return <button onClick={() => login()}>Sign in</button>
 *
 *   return <div>Welcome, {displayName}! <button onClick={logout}>Sign out</button></div>
 * }
 * ```
 */
export function useAuth(): UseAuthReturn {
  const context = useContext(AuthContext)
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
